
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from 'react-helmet';
import { Link } from "react-router-dom";
import { ArrowLeft, Play, Calendar, Menu, X, LogIn, ArrowRight, ChevronDown, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Badge component for video tags
function Badge({ children, className = "" }) {
  return (
    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${className}`}>
      {children}
    </span>
  );
}

// Button component with enhanced styling
function Button({ children, onClick, className = "", ...props }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-4 py-2 rounded-lg font-medium shadow-sm transition-all duration-300 transform hover:scale-105 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export default function VideoDemosPage() {
  // Define videos array first
  const videos = [
    // Features Category (previously "featured")
    {
      title: "Iron Workers/IMPACT Conference: Is AI Freaking You Out?",
      videoId: "GPj4oHnMT1E",
      category: "features",
      featured: true,
      description:
        "Presented at the IMPACT Conference to 1,200+ steel industry leaders—see AI in action.",
    },
    {
      title: "AI is the Future of Construction - Here’s How to Prepare",
      videoId: "o0xbD0clcCY",
      category: "features",
      featured: true,
      description: "A podcast exploring how AI is shaping the future of construction.",
    },
    // Podcasts Category
    {
      title: "How Custom AI Is Transforming Construction",
      videoId: "Dknjyq8JMCM",
      category: "podcasts",
      description: "Dive into the transformation of construction with custom AI solutions.",
    },
    {
      title: "The Story Behind A Résumé",
      videoId: "2u5NlFddrmg",
      category: "podcasts",
      description: "A podcast uncovering the story behind creating an effective résumé.",
    },
    // Tool Demos Category (previously "product")
    {
      title: "Myte Social Computer Operator Demo",
      videoId: "iiOcc95pjTc",
      category: "tool-demos",
      description: "A short demo showcasing Myte Social’s computer operator features.",
    },
    {
      title: "Myte Material Request Video Demo",
      videoId: "Tq5e2_Wfe8U",
      category: "tool-demos",
      description: "See how voice-enabled material requests streamline workflows.",
    },
    {
      title: "Myte Timesheets Video Demo",
      videoId: "pW2yJ2dujAY",
      category: "tool-demos",
      description: "Voice-driven timesheets demo—save hours every week.",
    },
    // Conferences Category
    {
      title: "Iron Workers/IMPACT Conference: Is AI Freaking You Out?",
      videoId: "GPj4oHnMT1E",
      category: "conferences",
      description: "Replay of the IMPACT Conference talk on AI in construction.",
    },
  ];

  // Now define activeVideo state
  const [activeVideo, setActiveVideo] = useState(() => {
    const featuredVideo = videos.find((v) => v.featured);
    return featuredVideo
      ? { ...featuredVideo, videoUrl: `https://www.youtube.com/embed/${featuredVideo.videoId}?autoplay=1` }
      : null;
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [category, setCategory] = useState("all"); // Default to "features"
  const videoPlayerRef = useRef(null);

  // Handle video selection
  const handleVideoSelect = (video) => {
    const videoUrl = `https://www.youtube.com/embed/${video.videoId}?autoplay=1`;
    setActiveVideo({ ...video, videoUrl });
    if (window.innerWidth < 768 && videoPlayerRef.current) {
      videoPlayerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle scroll for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Filter videos by category
  const filteredVideos = category === "all" ? videos : videos.filter((v) => v.category === category);

  return (
    <>
      <Helmet>
        <title>Video Demos – Myte Construction | Myte Group</title>
        <meta name="description" content="Watch video demos of Myte Group's AI-powered construction tools, including voice-driven timesheets and material requests." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://mytegroup.ai/myteconstruction/video-demos" />
        <meta property="og:title" content="Video Demos – Myte Construction" />
        <meta property="og:description" content="Watch video demos of Myte Group's AI-powered construction tools, including voice-driven timesheets and material requests." />
        <meta property="og:url" content="https://mytegroup.ai/myteconstruction/video-demos" />
        <meta property="og:image" content="https://mytegroup.ai/og-thumbnail.png" />
        <meta property="og:image:alt" content="Myte Construction video demos preview" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Video Demos – Myte Construction" />
        <meta name="twitter:description" content="Watch video demos of Myte Group's AI-powered construction tools, including voice-driven timesheets and material requests." />
        <meta name="twitter:image" content="https://mytegroup.ai/og-thumbnail.png" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white">
      {/* Header (Matches LandingPage.js) */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-[#0e1d34]/90 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/myteconstruction" className="flex items-center">
            <img
              src="/LogoFile.png"
              alt="Myte Construction Logo"
              className="w-36 h-auto max-w-full object-contain"
            />
          </Link>
          <nav className="flex items-center gap-6">
            <div className="hidden md:flex gap-6">
              <Link to="/myteconstruction/#products" className="text-sm font-medium text-blue-200 hover:text-white">
                Products
              </Link>
              <Link to="/myteconstruction/#features" className="text-sm font-medium text-blue-200 hover:text-white">
                Features
              </Link>
              <Link to="/myteconstruction/#pricing" className="text-sm font-medium text-blue-200 hover:text-white">
                Pricing
              </Link>
              <Link to="/myteconstruction/video-demos" className="text-sm font-medium text-white">
                Video Demos
              </Link>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/myteconstruction/login"
                className="inline-flex items-center px-3 py-2 bg-transparent hover:bg-[#3080c1]/20 text-white rounded-lg"
              >
                <LogIn className="w-4 h-4 mr-2" /> Login
              </Link>
              <a
                href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg"
              >
                <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
              </a>
            </div>
            <Button
              className="md:hidden bg-transparent text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </nav>
        </div>
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0e1d34]/95 backdrop-blur-md p-4"
            >
              <nav className="flex flex-col gap-4 max-w-7xl mx-auto">
                <Link to="/myteconstruction/#products" className="text-lg text-blue-200 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                  Products
                </Link>
                <Link to="/myteconstruction/#features" className="text-lg text-blue-200 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                  Features
                </Link>
                <Link to="/myteconstruction/#pricing" className="text-lg text-blue-200 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                  Pricing
                </Link>
                <Link to="/myteconstruction/video-demos" className="text-lg text-white" onClick={() => setIsMenuOpen(false)}>
                  Video Demos
                </Link>
                <Link
                  to="/myteconstruction/login"
                  className="flex items-center px-4 py-3 bg-transparent hover:bg-[#3080c1]/20 text-white rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <LogIn className="w-4 h-4 mr-2" /> Login
                </Link>
                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-4 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="pt-24 md:pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-8">
            <Button className="text-blue-200 hover:text-white bg-transparent p-0">
              <Link to="/myteconstruction" className="inline-flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Landing Page
              </Link>
            </Button>
          </div>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold mb-4"
            >
              Myte Construction Video Showcase
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-blue-200 max-w-3xl mx-auto"
            >
              Discover how Myte Construction leverages AI through our curated video collection—demos, podcasts, and conference talks.
            </motion.p>
          </div>

          {/* Video Player and List */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Video Player */}
            <div
              ref={videoPlayerRef}
              className="lg:col-span-2 bg-[#0e1d34]/80 rounded-xl p-6 border border-[#3080c1]/30 shadow-lg"
            >
              {activeVideo ? (
                <>
                  <div className="aspect-video w-full mb-4 bg-black rounded-lg overflow-hidden shadow-md">
                    <iframe
                      src={activeVideo.videoUrl}
                      className="w-full h-full"
                      title={activeVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {activeVideo.featured && (
                          <Badge className="bg-amber-600/20 text-amber-400">
                            <Star className="w-3 h-3 mr-1" /> Featured
                          </Badge>
                        )}
                        <Badge className="bg-[#3080c1]/20 text-[#3080c1] capitalize">
                          {activeVideo.category.replace("-", " ")}
                        </Badge>
                      </div>
                      <h2 className="text-2xl font-bold text-blue-100">{activeVideo.title}</h2>
                    </div>
                    <p className="text-blue-200">{activeVideo.description}</p>
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <Button className="bg-[#3080c1] hover:bg-[#2563eb] text-white">
                        <a
                          href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center"
                        >
                          <Calendar className="mr-2 w-5 h-5" /> Schedule Your Demo
                        </a>
                      </Button>
                      <Button className="border border-white text-white hover:bg-white/10">
                        <Link to="/myteconstruction" className="inline-flex items-center">
                          Return to Landing Page
                        </Link>
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                  <Play className="w-16 h-16 text-[#3080c1] mb-4 opacity-50" />
                  <p className="text-xl text-blue-200">Select a video to watch</p>
                </div>
              )}
            </div>

            {/* Video List */}
            <div className="lg:col-span-1 bg-[#0e1d34]/80 rounded-xl p-6 border border-[#3080c1]/30 shadow-lg h-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-blue-100">Video Library</h2>
                <div className="relative">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="bg-[#1e3a8a] text-white rounded-lg pl-3 pr-8 py-2 text-sm border border-[#3080c1]/30 appearance-none focus:outline-none focus:ring-2 focus:ring-[#3080c1]"
                  >
                    <option value="all">All Videos</option>
                    <option value="features">Features</option>
                    <option value="podcasts">Podcasts</option>
                    <option value="tool-demos">Tool Demos</option>
                    <option value="conferences">Conferences</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
                </div>
              </div>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {filteredVideos.map((video, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`flex gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      activeVideo && activeVideo.videoId === video.videoId
                        ? "bg-[#3080c1]/20 border border-[#3080c1]"
                        : "hover:bg-[#1e3a8a]/30 border border-transparent"
                    }`}
                    onClick={() => handleVideoSelect(video)}
                  >
                    <div className="relative flex-shrink-0 w-24 h-16 rounded overflow-hidden">
                      <img
                        src={`https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">{video.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {video.featured && (
                          <Badge className="bg-amber-600/20 text-amber-400 text-[0.6rem]">
                            <Star className="w-2 h-2 mr-0.5" /> Featured
                          </Badge>
                        )}
                        <span className="text-xs text-blue-300 capitalize">
                          {video.category.replace("-", " ")}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section (Revamped with Icons and Buttons) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mt-12 bg-gradient-to-br from-[#1e3a8a]/50 to-[#0e1d34]/50 p-8 rounded-xl border border-[#3080c1]/30 shadow-lg"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-100">
              Ready to Transform Your Construction Workflow?
            </h2>
            <p className="text-blue-200 mb-8 max-w-2xl mx-auto">
              Join 1,200+ leaders who’ve seen the power of Myte’s AI solutions firsthand.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <Button className="bg-[#3080c1] hover:bg-[#2563eb] text-white px-6 py-3 text-lg">
                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center w-full justify-center"
                >
                  <Calendar className="mr-2 w-5 h-5" /> Schedule Your Demo
                </a>
              </Button>
              <Button className="border border-white text-white hover:bg-white/10 px-6 py-3 text-lg">
                <a href="https://www.mytecody.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center w-full justify-center">
                  <ArrowRight className="mr-2 w-5 h-5" /> Start Your Roadmap
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer – Updated with Landing Page section links */}
      <footer className="py-12 px-4 bg-[#0e1d34] border-t border-[#3080c1]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="font-bold text-lg text-blue-100">Myte Construction</span>
              </div>
              <p className="text-sm text-blue-200 mb-4 max-w-md">
                Revolutionizing payroll management for field construction crews with AI-driven,
                voice-enabled technology.
              </p>
              <p className="text-xs text-blue-300">
                © {new Date().getFullYear()} Myte Construction. All rights reserved.
              </p>
            </div>
            <div className="md:col-span-3">
              <h3 className="text-lg font-bold mb-4 text-blue-100">Quick Links</h3>
              <ul className="flex flex-col gap-2">
                <li>
                  <Link to="/myteconstruction/#products" className="text-blue-200 hover:text-white">
                    Products
                  </Link>
                </li>
                <li>
                  <Link to="/myteconstruction/#features" className="text-blue-200 hover:text-white">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/myteconstruction/#pricing" className="text-blue-200 hover:text-white">
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link to="/myteconstruction/#community" className="text-blue-200 hover:text-white">
                    Trusted by Innovators
                  </Link>
                </li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <h3 className="text-lg font-bold mb-4 text-blue-100">Contact</h3>
              <ul className="flex flex-col gap-2">
                <li>
                  <a
                    href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-200 hover:text-white"
                  >
                    Schedule a Call
                  </a>
                </li>
                <li>
                  <Link to="/myteconstruction/video-demos" className="text-blue-200 hover:text-white">
                    Video Demos
                  </Link>
                </li>
                <li>
                  <Link to="/myteconstruction/login" className="text-blue-200 hover:text-white">
                    Login
                  </Link>
                </li>
              </ul>
            </div>
            <div className="md:col-span-2 flex justify-center md:justify-end items-start">
              <Link to="/myteconstruction" className="flex items-center">
                <img src="/LogoFile.png" alt="Myte Construction Logo" className="w-24 h-auto" />
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom Scrollbar Styling */}
     <style>{`
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(30, 58, 138, 0.1);
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(48, 128, 193, 0.5);
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(48, 128, 193, 0.8);
      }
    `}</style>
    </div>
  </>
  );
}
